import {FC} from 'react';
import {useField} from 'formik';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import dayjs from 'dayjs';
import useShow, {TShow} from '../../../../../hooks/useShow';
import {RootState} from '../../../../../redux/store.type';
import {TSlot} from '../../../../../redux/timetable/timetable.type';
import {useTimetableDays} from '../../../hooks/useTimetableDays';
import {getStartOfDay} from '../../../../../library/dates';
import {stopWatch} from '../../../../../assets/symbols/symbols/Symbols.css';
import SelectPopup from '../../../../../component/selectPopup/SelectPopup';
import AddSlotPopup from './AddSlotPopup';

const formatTime = (timeStart: number) => dayjs.unix(timeStart).format('HH:mm');

interface TimeSlotSelectPopupProps {
    show: TShow;
    name?: string;
}

const TimeSlotSelectPopup: FC<TimeSlotSelectPopupProps> = ({show, name = 'tsid'}) => {
    const {t} = useTranslation('timetable');
    const [field, , helpers] = useField<number | null>(name);
    const [dayField] = useField<number>('dayIndex');
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const {conferenceDays} = useTimetableDays();
    const addShow = useShow();

    // Filter by the date selected in the form (dayIndex), so the hours list
    // updates when the lecture's date is changed while editing.
    const selectedDay = conferenceDays[dayField.value];

    const items = slots
        .filter((s: TSlot) =>
            selectedDay ? getStartOfDay(s.timeStart) === getStartOfDay(selectedDay.timestamp) : true
        )
        .map((s: TSlot) => ({id: s.tsid, text: formatTime(s.timeStart)}));
    const handleAdd = (tsid: number) => { helpers.setValue(tsid); };

    return (
        <>
            {!addShow.isOpen && (
                <SelectPopup
                    show={show}
                    title={t('selectSlotTitle')}
                    items={items}
                    selectedIds={field.value !== null ? [field.value] : []}
                    onConfirm={ids => helpers.setValue(ids[0] ?? null)}
                    addAction={{text: t('addSlot'), emptyText: t('addNewSlot'), onClick: addShow.open}}
                    hideSearch
                    empty={{icon: stopWatch, text: t('noSlotsYet')}}
                />
            )}
            <AddSlotPopup show={addShow} dayTimestamp={selectedDay?.timestamp ?? 0} onAdd={handleAdd} />
        </>
    );
};

export default TimeSlotSelectPopup;
